import { Subject } from "rxjs";

export class SelectionModel<T> {
  private _selection = new Set<T>();
  private _changed = false;

  /** Cache for the array value of the selected items. */
  private _selected: T[] | null = null;

  readonly changed = new Subject<T[]>();

  get selected(): T[] {
    if (!this._selected) {
      this._selected = Array.from(this._selection.values());
    }

    return this._selected;
  }

  constructor(
    private multiple: boolean = false,
    initiallySelectedValues?: T[],
    public compareWith?: (o1: T, o2: T) => boolean
  ) {
    if (initiallySelectedValues && initiallySelectedValues.length) {
      if (multiple) {
        initiallySelectedValues.forEach((value) => this._markSelected(value));
      } else {
        this._markSelected(initiallySelectedValues[0]);
      }
    }
  }

  select(...values: T[]): boolean | void {
    this._verifyValueAssignment(values);

    values.forEach(e => this._markSelected(e));
    
    if(this._changed) {
      this._emitChangeEvent();
      this._changed = false;
      return true;
    }

    return false;
  }

  deselect(...values: T[]): boolean | void {
    this._verifyValueAssignment(values);

    values.forEach(e => this._unmarkSelected(e));

    if(this._changed) {
      this._emitChangeEvent();
      this._changed = false;
      return true;
    }

    return false;
  }

  toggle(value: T): boolean | void {
    return this.isSelected(value) ? this.deselect(value) : this.select(value)
  }

  clear(): boolean | void {
    this._unmarkAll();
    
    if(this._changed) {
      this._emitChangeEvent();
      this._changed = false;
      return true;
    }

    return false;
  }

  isEmpty() {
    return this._selection.size == 0
  }

  isSelected(value: T) {
    return this._isSelected(this._getConcreteValue(value));
  }

  isMultipleSelection() {
    return this.multiple;
  }
  
  private _isSelected(concretValue: T) {
    return this._selection.has(concretValue);
  }

  private _getConcreteValue(inputValue: T) {
    if(!this.compareWith) {
      return inputValue;
    }
    for (let selectedValue of this._selection) {
      if (this.compareWith!(inputValue, selectedValue)) {
        return selectedValue;
      }
    }
    return inputValue;
  }

  private _emitChangeEvent() {
    // Clear the selected values so they can be re-cached.
    this._selected = null;
    
    this.changed.next(this.selected);
  }

  private _markSelected(value: T): void {
    value = this._getConcreteValue(value);
    if(!this._isSelected(value)) {
      if (!this.multiple) {
        this._unmarkAll();
      }

      this._selection.add(value);

      this._changed = true;
    }
  }

  private _unmarkAll() {
    if(!this.isEmpty()) {
      this._selection.clear();
      this._changed = true;
    }
  }

  private _unmarkSelected(value: T) {
    value = this._getConcreteValue(value);
    if (this._isSelected(value)) {
      this._selection.delete(value);
      this._changed = true;
    }
  }

  private _verifyValueAssignment(values: T[]) {
    if (values.length > 1 && !this.multiple) {
      throw getMultipleValuesInSingleSelectionError();
    }
  }

}

export function getMultipleValuesInSingleSelectionError() {
  return Error('Cannot pass multiple values into SelectionModel with single-value mode.');
}